import { create } from "zustand";

export type WorkspaceView =
  | "learn"
  | "dashboard"
  | "tutor"
  | "practice"
  | "exam"
  | "topics"
  | "flashcards"
  | "score"
  | "resources"
  | "settings";

interface WorkspaceState {
  view: WorkspaceView;
  setView: (v: WorkspaceView) => void;
  /** Live tutor session launched from Topics (or custom topic). */
  tutorLaunchQueue: TutorSession | null;
  tutorSessionNonce: number;
  startTutorSession: (session: TutorSession) => void;
  consumeTutorLaunch: () => TutorSession | null;
  /** Chatbot sidebar (Ask Maya anything) — separate from tutor. */
  insightsOpen: boolean;
  setInsightsOpen: (open: boolean) => void;
  pendingChatbotPrompt: string | null;
  askMaya: (prompt: string) => void;
  consumeChatbotPrompt: () => string | null;
}

export interface TutorSession {
  topicId?: string;
  topicName: string;
  section: "quant" | "verbal" | "di" | "mindset" | "custom";
  lessonPrompt: string;
}

export const useWorkspace = create<WorkspaceState>((set, get) => ({
  view: "learn",
  setView: (view) => set({ view }),
  tutorLaunchQueue: null,
  tutorSessionNonce: 0,
  startTutorSession: (session) =>
    set((s) => ({
      tutorLaunchQueue: session,
      tutorSessionNonce: s.tutorSessionNonce + 1,
      view: "tutor",
    })),
  consumeTutorLaunch: () => {
    const launch = get().tutorLaunchQueue;
    if (launch) set({ tutorLaunchQueue: null });
    return launch;
  },
  insightsOpen: typeof localStorage !== "undefined" && localStorage.getItem("gslm-insights-open") !== "false",
  setInsightsOpen: (insightsOpen) => {
    localStorage.setItem("gslm-insights-open", String(insightsOpen));
    set({ insightsOpen });
  },
  pendingChatbotPrompt: null,
  askMaya: (prompt) =>
    set({
      pendingChatbotPrompt: prompt.trim(),
      insightsOpen: true,
    }),
  consumeChatbotPrompt: () => {
    const p = get().pendingChatbotPrompt;
    if (p) set({ pendingChatbotPrompt: null });
    return p;
  },
}));
